import { useRef, useState, type PointerEvent } from 'react';

export interface PhotoPosition { x: number; y: number }

const CENTRE: PhotoPosition = { x: 0.5, y: 0.5 };
const clamp = (v: number) => Math.min(1, Math.max(0, v));

/**
 * Dragging a photo inside its slot. The position is CSS object-position as a
 * 0–1 share of the overflow, so the PDF can crop the same way. Only the axis
 * the photo overflows on moves; the server keeps the result on pointer up.
 */
export function usePhotoPan(saved: PhotoPosition | null, onMove?: (position: PhotoPosition) => void) {
  const [live, setLive] = useState<PhotoPosition | null>(null);
  const drag = useRef<{ px: number; py: number; from: PhotoPosition; overflow: [number, number] } | null>(null);
  const position = live ?? saved ?? CENTRE;

  function onPointerDown(e: PointerEvent<HTMLImageElement>) {
    if (!onMove) return;
    const img = e.currentTarget;
    if (!img.naturalWidth || !img.naturalHeight) return;
    const scale = Math.max(img.clientWidth / img.naturalWidth, img.clientHeight / img.naturalHeight);
    const overflow: [number, number] = [img.naturalWidth * scale - img.clientWidth, img.naturalHeight * scale - img.clientHeight];
    if (overflow[0] < 1 && overflow[1] < 1) return;
    e.preventDefault();
    img.setPointerCapture(e.pointerId);
    drag.current = { px: e.clientX, py: e.clientY, from: position, overflow };
  }

  function onPointerMove(e: PointerEvent<HTMLImageElement>) {
    const d = drag.current;
    if (!d) return;
    // Dragging the photo right uncovers its left side: the position goes down.
    setLive({
      x: d.overflow[0] < 1 ? d.from.x : clamp(d.from.x - (e.clientX - d.px) / d.overflow[0]),
      y: d.overflow[1] < 1 ? d.from.y : clamp(d.from.y - (e.clientY - d.py) / d.overflow[1]),
    });
  }

  function onPointerUp() {
    const d = drag.current;
    drag.current = null;
    if (!d || !live || !onMove) return;
    if (live.x !== d.from.x || live.y !== d.from.y) onMove({ x: round(live.x), y: round(live.y) });
  }

  return {
    position,
    handlers: onMove ? { onPointerDown, onPointerMove, onPointerUp, onPointerCancel: onPointerUp } : {},
  };
}

const round = (v: number) => Math.round(v * 1000) / 1000;
